import { TIndirectTaxTransactionType } from '@commenda/api/indirect-tax/transactions/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@commenda/kakashi';

import { StackedDataPoint } from '../../../components';
import { IndirectTaxTransactionHelper } from '../helpers';
import { TIndirectTaxTransactionLineItem } from '../types';
import { IndirectTaxLineItemTaxBreakdown } from './IndirectTaxLineItemTaxBreakdown';
import { IndirectTaxLineItemTaxLogic } from './IndirectTaxLineItemTaxLogic';

interface IndirectTaxTransactionLineItemSheetProps {
  lineItem: TIndirectTaxTransactionLineItem | null;
  isOpen: boolean;
  onClose: () => void;
  currency: string;
  defaultValues?: {
    productTaxCode?: string;
  };
  onViewRegistration?: (registrationId: string) => void;
  onViewCustomer?: (customerId: string) => void;
  onViewProduct?: (productId: string) => void;
  onGenerateTaxBreakdown?: () => Promise<void>;
  isGeneratingTaxBreakdown?: boolean;
  destinationCountryCode?: string;
  transactionType?: TIndirectTaxTransactionType;
}

const IndirectTaxTransactionLineItemSheet = ({
  lineItem,
  isOpen,
  onClose,
  currency,
  defaultValues,
  onViewRegistration,
  onViewCustomer,
  onViewProduct,
  onGenerateTaxBreakdown,
  isGeneratingTaxBreakdown,
  destinationCountryCode,
  transactionType,
}: IndirectTaxTransactionLineItemSheetProps) => {
  const hideTaxBreakdown = lineItem?.lineTotal === 0;
  const taxBreakdownUnavailableReason =
    !IndirectTaxTransactionHelper.isTaxBreakdownSupportedDestination(
      destinationCountryCode
    )
      ? 'Tax breakdown cannot be generated for this destination region.'
      : transactionType &&
          transactionType !== TIndirectTaxTransactionType.SALE &&
          transactionType !== TIndirectTaxTransactionType.REFUND
        ? 'Tax breakdown cannot be generated for this transaction type.'
        : null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        hideOverlay
        className="sm:max-w-[480px] m-xl h-[calc(100%-2rem)] rounded-lg border border-border-primary shadow-xl p-none overflow-hidden flex flex-col gap-none"
      >
        <SheetHeader className="p-3xl border-b border-border-primary flex-row items-center justify-between">
          <SheetTitle className="txt-medium-bold text-text-primary">
            Line item
          </SheetTitle>
        </SheetHeader>

        {lineItem && (
          <div className="flex-1 overflow-y-auto">
            <Accordion
              type="multiple"
              defaultValue={['details', 'tax-logic', 'tax-breakdown']}
            >
              <AccordionItem
                value="details"
                className="bg-transparent border-b"
              >
                <AccordionTrigger className="txt-extra-large-bold text-text-primary [&>svg]:text-text-tertiary !p-3xl">
                  Product Details
                </AccordionTrigger>
                <AccordionContent>
                  <StackedDataPoint
                    label="Product name"
                    value={lineItem.productName}
                  />
                  <StackedDataPoint
                    label="Product SKU"
                    value={lineItem.productSku || '—'}
                  />
                  <StackedDataPoint
                    label="Tax code"
                    value={
                      <Badge variant="gray" size="sm">
                        {lineItem.taxCode ||
                          `Default - ${defaultValues?.productTaxCode}`}
                      </Badge>
                    }
                  />
                  <StackedDataPoint
                    label="Quantity"
                    value={lineItem.quantity}
                  />
                  <StackedDataPoint
                    label="Amount"
                    value={`${IndirectTaxTransactionHelper.formatCurrency(lineItem.lineTotal)} ${currency}`}
                  />
                </AccordionContent>
              </AccordionItem>

              {lineItem.taxBreakdown && lineItem.taxBreakdown.length > 0 && (
                <AccordionItem
                  value="tax-logic"
                  className="bg-transparent border-b"
                >
                  <AccordionTrigger className="txt-extra-large-bold text-text-primary [&>svg]:text-text-tertiary !p-3xl">
                    <span className="flex items-center gap-md">
                      Tax Logic
                      {lineItem.taxLogic && (
                        <Badge variant="success" size="sm">
                          Applied
                        </Badge>
                      )}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <IndirectTaxLineItemTaxLogic
                      taxLogic={lineItem.taxLogic}
                      onViewRegistration={onViewRegistration}
                      onViewCustomer={onViewCustomer}
                      onViewProduct={onViewProduct}
                    />
                  </AccordionContent>
                </AccordionItem>
              )}

              {!hideTaxBreakdown && (
                <AccordionItem
                  value="tax-breakdown"
                  className="bg-transparent border-b-0"
                >
                  <AccordionTrigger className="txt-extra-large-bold text-text-primary [&>svg]:text-text-tertiary !p-3xl">
                    Tax Breakdown
                  </AccordionTrigger>
                  <AccordionContent>
                    <IndirectTaxLineItemTaxBreakdown
                      rows={lineItem.taxBreakdown ?? []}
                      onGenerateTaxBreakdown={onGenerateTaxBreakdown}
                      isGenerating={isGeneratingTaxBreakdown}
                      unavailableReason={taxBreakdownUnavailableReason}
                    />
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export { IndirectTaxTransactionLineItemSheet };
